import { motion, AnimatePresence, useInView } from "motion/react";
import { useState, useRef, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import checkoutScreen from "../assets/LandingHero/velora-checkout.webp";
import variationElectric from "../assets/LandingHero/velora-variation-1.webp";
import variationDark from "../assets/LandingHero/velora-variation-2.webp";
import variationLight from "../assets/LandingHero/velora-variation-3.webp";

import CollaborativeCursors from "./CollaborativeCursors";
import CustomCursor from "./CustomCursor";
import { useReducedMotion } from "./ui/use-reduced-motion";

// Real checkout designs made in Flowstep (the "Velora" store).
const variationScreens = [variationElectric, variationDark, variationLight];

const CMD_1 = "Show me our latest checkout page";
const CMD_2 = "Now create 3 variations of that";
const CMD_3 = "Make them match our design system";

export default function GradientSection({
  headingScale = "star",
  title = "Iterate visually on your codebase",
  intro = "Import components in seconds.",
}: {
  /**
   * "star" is the landing's showcase scale; "section" matches the 4rem
   * section-heading system on pages whose H1 is smaller than the landing's.
   */
  headingScale?: "star" | "section";
  /** Page-specific heading/intro; defaults are the landing's copy. */
  title?: string;
  intro?: string;
} = {}) {
  const [showCustomCursor, setShowCustomCursor] = useState(false);
  const [typed1, setTyped1] = useState("");
  const [typed2, setTyped2] = useState("");
  const [typed3, setTyped3] = useState("");
  const [resp1, setResp1] = useState(false);
  const [resp2, setResp2] = useState(false);
  const [resp3, setResp3] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [variationsShown, setVariationsShown] = useState(0);
  const [loadingVariations, setLoadingVariations] = useState(false);
  const [restyledCount, setRestyledCount] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!isInView) return;

    if (prefersReducedMotion) {
      setTyped1(CMD_1);
      setTyped2(CMD_2);
      setTyped3(CMD_3);
      setResp1(true);
      setResp2(true);
      setResp3(true);
      setShowCheckout(true);
      setVariationsShown(3);
      setRestyledCount(3);
      setLoadingVariations(false);
      return;
    }

    const timers: number[] = [];
    const at = (ms: number, fn: () => void) =>
      timers.push(window.setTimeout(fn, ms));
    const typeInto = (
      cmd: string,
      setter: (value: string) => void,
      startMs: number,
    ) => {
      for (let i = 0; i < cmd.length; i++) {
        at(startMs + i * 30, () => setter(cmd.slice(0, i + 1)));
      }
    };

    // Command 1: visualise the checkout page
    typeInto(CMD_1, setTyped1, 400);
    at(1800, () => setResp1(true));
    at(2400, () => setShowCheckout(true));

    // Command 2: three variations land on the canvas
    typeInto(CMD_2, setTyped2, 3600);
    at(4900, () => setResp2(true));
    at(5300, () => setVariationsShown(1));
    at(5700, () => setVariationsShown(2));
    at(6100, () => setVariationsShown(3));

    // Command 3: the drifted variations snap to the design system. The
    // rainbow generating border runs while the restyle is "in flight".
    typeInto(CMD_3, setTyped3, 7300);
    at(8700, () => setLoadingVariations(true));
    at(10400, () => setResp3(true));
    at(10700, () => setRestyledCount(1));
    at(11000, () => setRestyledCount(2));
    at(11300, () => setRestyledCount(3));

    return () => timers.forEach(clearTimeout);
  }, [isInView, prefersReducedMotion]);

  return (
    <DndProvider backend={HTML5Backend}>
      {showCustomCursor && <CustomCursor name="You" color="#10B981" />}
      <section
        ref={sectionRef}
        className="relative hidden min-h-screen w-full flex-col items-center justify-center px-4 py-16 md:px-8 lg:flex lg:px-16 lg:py-28"
      >
        {/* Header */}
        <div className="mx-auto mb-16 max-w-[1600px] text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-6 leading-[0.95] tracking-tight text-gray-900 dark:text-white"
            style={{
              fontSize:
                headingScale === "star"
                  ? "clamp(2.5rem, 6.5vw, 5.5rem)"
                  : "clamp(2.25rem, 4.5vw, 4rem)",
              fontWeight: "600",
            }}
          >
            {title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mx-auto max-w-3xl leading-relaxed text-gray-600 dark:text-gray-300"
            style={{
              fontSize:
                headingScale === "star"
                  ? "clamp(1.125rem, 2.5vw, 1.5rem)"
                  : "clamp(1.125rem, 2vw, 1.25rem)",
            }}
          >
            {intro}
          </motion.p>
        </div>

        {/* Canvas */}
        <div className="relative w-full max-w-[1600px]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative min-h-[800px] overflow-hidden rounded-2xl border border-gray-900/10 bg-white/10 p-8 backdrop-blur-sm lg:p-16 dark:border-white/15 dark:bg-white/5"
            style={{ cursor: showCustomCursor ? "none" : "auto" }}
            onMouseEnter={() => setShowCustomCursor(true)}
            onMouseLeave={() => setShowCustomCursor(false)}
          >
            {/* Canvas grid pattern */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage: `
                  linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)
                `,
                backgroundSize: "40px 40px",
              }}
            />

            {/* Collaborative Cursors */}
            <CollaborativeCursors />

            {/* Terminal + checkout as a centered pair, so the canvas
                composition holds at any width. The checkout slot is
                reserved up front to avoid a shift when it lands. */}
            <div className="relative flex items-start justify-center gap-16 pt-8 xl:gap-24">
              {/* Agent terminal */}
              <div className="mt-12 w-[420px] shrink-0 overflow-hidden rounded-xl border border-white/10 bg-[#0d1017] shadow-2xl">
                <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
                  <div className="h-2.5 w-2.5 rounded-full bg-yellow-500" />
                  <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
                  <span className="ml-auto font-mono text-[11px] text-gray-500">
                    flowstep — agent
                  </span>
                </div>
                <div className="min-h-[236px] space-y-2 p-4 font-mono text-[13px] leading-relaxed">
                  <div className="text-gray-200">
                    <span className="text-gray-500">$ </span>
                    {typed1}
                    {!resp1 && (
                      <span className="animate-pulse text-gray-400">▍</span>
                    )}
                  </div>
                  {resp1 && (
                    <div className="text-green-400">
                      ✓ Found checkout.tsx — rendering on the canvas
                    </div>
                  )}
                  {resp1 && (
                    <div className="text-gray-200">
                      <span className="text-gray-500">$ </span>
                      {typed2}
                      {!resp2 && (
                        <span className="animate-pulse text-gray-400">▍</span>
                      )}
                    </div>
                  )}
                  {resp2 && (
                    <div className="text-green-400">
                      ✓ 3 variations added to the canvas
                    </div>
                  )}
                  {resp2 && (
                    <div className="text-gray-200">
                      <span className="text-gray-500">$ </span>
                      {typed3}
                      {!resp3 && (
                        <span className="animate-pulse text-gray-400">▍</span>
                      )}
                    </div>
                  )}
                  {resp3 && (
                    <div className="text-green-400">
                      ✓ Restyled to your theme — colors, radius, type
                    </div>
                  )}
                </div>
              </div>

              {/* Checkout page, visualised from the codebase */}
              <div className="w-[380px] shrink-0">
                <AnimatePresence>
                  {showCheckout && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.85, y: 16 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                      <div className="mb-1.5 text-[11px] font-medium text-gray-400">
                        Checkout
                      </div>
                      <img
                        src={checkoutScreen}
                        alt="Checkout page design"
                        width={1200}
                        height={1076}
                        loading="lazy"
                        decoding="async"
                        className="h-auto w-full rounded-lg shadow-2xl"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Three variations landing beside it */}
            <div className="absolute inset-x-0 top-[500px] flex justify-center gap-6">
              {variationScreens.map((screen, i) => (
                <AnimatePresence key={screen}>
                  {variationsShown > i && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.85, y: 16 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="w-[360px]"
                    >
                      <div className="mb-1.5 text-[11px] font-medium text-gray-400">
                        Variation {i + 1}
                      </div>
                      <div className="relative">
                        {/* Flowstep's generating border — rainbow ring that
                            spins while the restyle is in flight */}
                        <AnimatePresence>
                          {loadingVariations && restyledCount <= i && (
                            <motion.div
                              aria-hidden
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{
                                opacity: 0,
                                transition: { duration: 0.3 },
                              }}
                              transition={{ duration: 0.4 }}
                              className="pointer-events-none absolute -inset-[2.5px] overflow-hidden rounded-[10px]"
                            >
                              <div
                                className="absolute -inset-full animate-[spin_1.8s_linear_infinite]"
                                style={{
                                  background:
                                    "conic-gradient(#0247FE, #8B5CF6, #EC4899, #F59E0B, #10B981, #0247FE)",
                                }}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <img
                          src={screen}
                          alt={`Checkout variation ${i + 1}`}
                          width={1200}
                          height={675}
                          loading="lazy"
                          decoding="async"
                          className="relative h-auto w-full rounded-lg shadow-2xl"
                        />
                        {/* Selection pulse as the design system applies */}
                        {restyledCount > i && (
                          <motion.div
                            aria-hidden
                            initial={{ opacity: 0.9 }}
                            animate={{ opacity: 0 }}
                            transition={{ duration: 1, ease: "easeOut" }}
                            className="pointer-events-none absolute inset-0 rounded-lg ring-2 ring-[#0247fe]"
                          />
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </DndProvider>
  );
}
